import type { Server } from "@modelcontextprotocol/sdk/server/index.js";
import {
  CallToolRequestSchema,
  ErrorCode,
  ListToolsRequestSchema,
  McpError,
} from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";

import { listDocs, readDoc, readNextjsExampleCode, searchDocs } from "./docs.js";

const DEFAULT_API_BASE = "https://api.roottale.com";

const ReadDocInput = z.object({ path: z.string().min(1) }).strict();
const SearchDocsInput = z.object({ query: z.string().min(1) }).strict();
const ListPostsInput = z
  .object({
    limit: z.number().int().min(1).max(100).optional(),
    type: z.enum(["post", "page"]).optional(),
    cursor: z.string().optional(),
  })
  .strict();
const GetPostInput = z.object({ slugOrId: z.string().min(1) }).strict();

export const TOOL_DEFINITIONS = [
  {
    name: "listRootTaleDocs",
    description:
      "RootTale CMS 통합 문서 전체 목록(경로·제목·설명)을 반환합니다. " +
      "RootTale 연동 작업을 시작하기 전 가장 먼저 호출하세요.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
  },
  {
    name: "readRootTaleDoc",
    description:
      "RootTale CMS 통합 문서 1개를 경로로 읽습니다. " +
      "경로는 listRootTaleDocs 가 반환한 path 값을 사용하세요.",
    inputSchema: {
      type: "object",
      properties: {
        path: { type: "string", description: "문서 경로 (예: blog.md)" },
      },
      required: ["path"],
      additionalProperties: false,
    },
  },
  {
    name: "searchRootTaleDocs",
    description:
      "RootTale CMS 통합 문서 전체에서 정규식으로 검색합니다 (대소문자 무시). " +
      "매치된 파일 경로·라인 번호·라인 내용을 반환합니다.",
    inputSchema: {
      type: "object",
      properties: {
        query: { type: "string", description: "정규식 패턴 (예: revalidate|webhook)" },
      },
      required: ["query"],
      additionalProperties: false,
    },
  },
  {
    name: "readRootTaleNextjsExampleCode",
    description:
      "RootTale CMS를 외부 Next.js(App Router) 사이트에 연동하는 완전한 예시 코드 세트를 반환합니다 " +
      "(블로그 목록/상세, 웹훅 revalidate 라우트, RSS/사이트맵, 상담문의 서버 액션 등). " +
      "연동 코드를 작성하기 전 반드시 호출해 패턴을 따라 작성하세요.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
  },
  {
    name: "listPublishedPosts",
    description:
      "연동 검증용 — RootTale 공개 CMS API(GET /v1/cms/public/posts)로 발행된 글 목록을 조회합니다. " +
      "환경변수 ROOTTALE_API_KEY(rtlk_cust_*)가 필요합니다.",
    inputSchema: {
      type: "object",
      properties: {
        limit: { type: "number", description: "1-100, 기본 20" },
        type: { type: "string", enum: ["post", "page"] },
        cursor: { type: "string", description: "이전 응답의 next_cursor" },
      },
      additionalProperties: false,
    },
  },
  {
    name: "getPublishedPost",
    description:
      "연동 검증용 — RootTale 공개 CMS API(GET /v1/cms/public/posts/:identifier)로 발행된 글 1개를 " +
      "slug 또는 UUID로 조회합니다. 환경변수 ROOTTALE_API_KEY(rtlk_cust_*)가 필요합니다.",
    inputSchema: {
      type: "object",
      properties: {
        slugOrId: { type: "string", description: "글 slug 또는 UUID" },
      },
      required: ["slugOrId"],
      additionalProperties: false,
    },
  },
] as const;

function requireApiKey(): { apiKey: string; baseUrl: string } {
  const apiKey = process.env.ROOTTALE_API_KEY;
  if (!apiKey) {
    throw new McpError(
      ErrorCode.InvalidRequest,
      "ROOTTALE_API_KEY 환경변수가 없습니다. MCP 서버 설정의 env에 " +
        "rtlk_cust_* 키를 추가하세요 (발급: 어드민 설정 > 사이트 연결 키).",
    );
  }
  const baseUrl = (process.env.ROOTTALE_API_BASE ?? DEFAULT_API_BASE).replace(/\/+$/, "");
  return { apiKey, baseUrl };
}

async function fetchPublicApi(pathname: string, params?: Record<string, string>): Promise<unknown> {
  const { apiKey, baseUrl } = requireApiKey();
  const url = new URL(`${baseUrl}${pathname}`);
  for (const [key, value] of Object.entries(params ?? {})) {
    url.searchParams.set(key, value);
  }
  const response = await fetch(url, {
    headers: { authorization: `Bearer ${apiKey}` },
  });
  const body = await response.text();
  if (!response.ok) {
    throw new Error(`RootTale API ${response.status}: ${body.slice(0, 500)}`);
  }
  return JSON.parse(body);
}

function textResponse(text: string) {
  return { content: [{ type: "text" as const, text }] };
}

function jsonResponse(value: unknown) {
  return textResponse(JSON.stringify(value, null, 2));
}

export function registerTools(server: Server): void {
  server.setRequestHandler(ListToolsRequestSchema, async () => ({
    tools: TOOL_DEFINITIONS.map((t) => ({ ...t })),
  }));

  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const args = (request.params.arguments ?? {}) as Record<string, unknown>;
    try {
      switch (request.params.name) {
        case "listRootTaleDocs":
          return jsonResponse(await listDocs());
        case "readRootTaleDoc": {
          const input = ReadDocInput.parse(args);
          const content = await readDoc(input.path);
          if (content === null) {
            throw new McpError(
              ErrorCode.InvalidParams,
              `문서를 찾을 수 없습니다: ${input.path} — listRootTaleDocs로 경로를 확인하세요.`,
            );
          }
          return textResponse(content);
        }
        case "searchRootTaleDocs": {
          const input = SearchDocsInput.parse(args);
          return jsonResponse(await searchDocs(input.query));
        }
        case "readRootTaleNextjsExampleCode":
          return textResponse(await readNextjsExampleCode());
        case "listPublishedPosts": {
          const input = ListPostsInput.parse(args);
          const params: Record<string, string> = {};
          if (input.limit) params.limit = String(input.limit);
          if (input.type) params.type = input.type;
          if (input.cursor) params.cursor = input.cursor;
          return jsonResponse(await fetchPublicApi("/v1/cms/public/posts", params));
        }
        case "getPublishedPost": {
          const input = GetPostInput.parse(args);
          return jsonResponse(
            await fetchPublicApi(
              `/v1/cms/public/posts/${encodeURIComponent(input.slugOrId)}`,
            ),
          );
        }
        default:
          throw new McpError(
            ErrorCode.InvalidParams,
            `Unknown RootTale tool: ${request.params.name}`,
          );
      }
    } catch (error) {
      if (error instanceof McpError) throw error;
      const message = error instanceof Error ? error.message : String(error);
      return { isError: true, content: [{ type: "text" as const, text: message }] };
    }
  });
}
